import { Theme } from '@quillbot/design-system';
import { splitIntoSentences } from 'core/utils/sentenceSplitter';
import { findBestMatch, compareTwoStrings } from 'string-similarity';

// this method will search for longest matching words and highlight the input text.
const highlight = (inputStringRaw: string, outputStringRaw: string) => {
  let resultPara = '';
  const inputStringArr = inputStringRaw.split(' ');
  const outputStringArr = outputStringRaw.split(' ');

  let currentMatchedIndex = {
    start: 0,
    end: 0,
  };
  let startResultSearchWith = 0;
  let currentMatchedString = '';
  const permanentMatched: any = {};
  let matched = false;

  const setPermanentMatch = (startIndex: number, endIndex: number, currentWord: string) => {
    if (permanentMatched[currentMatchedString]) {
      delete permanentMatched[currentMatchedString];
      currentMatchedString += `${currentWord},`;
      permanentMatched[currentMatchedString] = {
        start: startIndex,
        end: endIndex,
      };
      return;
    }
    currentMatchedString += `${currentWord},`;
    permanentMatched[currentMatchedString] = {
      start: startIndex,
      end: endIndex,
    };
  };

  // Logic for searching common word sequence
  for (let i = 0; i < inputStringArr.length; i += 1) {
    for (let j = startResultSearchWith; j < outputStringArr.length; j += 1) {
      const tempInputString = inputStringArr[i]
        .trim()
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]/g, '');
      const tempOutputString = outputStringArr[j]
        .trim()
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]/g, '');

      if (
        !matched &&
        compareTwoStrings(tempInputString.toLowerCase(), tempOutputString.toLowerCase()) >= 0.9
      ) {
        matched = true;
        currentMatchedIndex.start = i;
        currentMatchedIndex.end = i;
        startResultSearchWith = j + 1;
        setPermanentMatch(currentMatchedIndex.start, currentMatchedIndex.end, tempInputString);
        break;
      } else if (
        matched &&
        compareTwoStrings(tempInputString.toLowerCase(), tempOutputString.toLowerCase()) >= 0.9
      ) {
        currentMatchedIndex.end = i;
        startResultSearchWith = j + 1;
        setPermanentMatch(currentMatchedIndex.start, currentMatchedIndex.end, tempInputString);
        break;
      } else if (matched && tempInputString.toLowerCase() !== tempOutputString.toLowerCase()) {
        matched = false;
        i -= 1; // stay on the same element
        startResultSearchWith = 0;
        currentMatchedIndex = {
          start: 0,
          end: 0,
        };
        currentMatchedString = '';
        break;
      }
    }
  }

  Object.keys(permanentMatched).forEach((key) => {
    if (permanentMatched[key].end - permanentMatched[key].start > 0) {
      inputStringArr[permanentMatched[key].start] = `<span style="color:#005DDA">${
        inputStringArr[permanentMatched[key].start]
      }`;
      inputStringArr[permanentMatched[key].end] = `${
        inputStringArr[permanentMatched[key].end]
      }</span>`;
    }
  });
  resultPara += inputStringArr.join(' ');
  return resultPara;
};

// This method will take input and output text, compare the sentences and map them together.
export const contextHighlighter = (
  originalInputString: string,
  originalOutputString: string,
  theme: any
) => {
  const matchedArray: any[] = [];
  const inputSentenceNodes = splitIntoSentences(originalInputString);
  const outputSentenceNodes = splitIntoSentences(originalOutputString);

  let inputSentenceArray = inputSentenceNodes.map((inputSentence) => inputSentence.raw);
  const outputSentenceArray = outputSentenceNodes
    .filter((outputSentence) => outputSentence.type === 'Sentence')
    .map((outputSentence) => outputSentence.raw);

  outputSentenceArray.forEach((outputSentence) => {
    let matchedInputSentence = '';
    matchedInputSentence = findBestMatch(outputSentence, inputSentenceArray).bestMatch.target;
    matchedArray.push({
      outputSentence,
      matchedInputSentence,
    });
  });

  inputSentenceArray = inputSentenceArray.map((inputSentence) => {
    const rawInputSentence = inputSentence;
    for (let i = 0; i < matchedArray.length; i += 1) {
      if (matchedArray[i].matchedInputSentence === rawInputSentence) {
        inputSentence = highlight(inputSentence, matchedArray[i].outputSentence);
      }
    }
    return inputSentence;
  });

  for (let index = 0; index < inputSentenceArray.length; index += 1) {
    if (inputSentenceArray[index].includes('<span style="color:#005DDA">')) {
      inputSentenceArray[index] = inputSentenceArray[index].replace(
        '</span>',
        '</span><span class="summary-educational-tooltip"></span>'
      );
      break;
    }
  }

  return inputSentenceArray
    .join('')
    .replace(
      /<span style="color:#005DDA">/g,
      `<span style="color:${theme.specialColors.LCS_COLOR}" title="Text included in the summary">`
    );
};

/**
 * Method to highlight selected keywords in text given.
 * @param text input/output text
 * @param keywords keywords selected by user
 * @param theme theme object of the app
 * @returns highlighted text
 */
export const keywordHighlight = (text: string, kw: Array<string>, theme: Theme) => {
  const keywords = [...kw];
  keywords.sort((a, b) => b.length - a.length);
  keywords.forEach((keyword) => {
    const regex = new RegExp(`(^| )(${keyword})(?=[ ?.,!]|<span|$)`, 'gim');
    text = text.replace(regex, `<span style="color:${theme.specialColors.SMZR_KEYWORD}">$&</span>`);
  });
  return text;
};
